import { Grid } from '@mui/material';
import { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import useDebounce from '../../../../hooks/useDebounce';
import { fetchAssociatedTestCases } from '../../../../modules/associations';
import { getTestSuites } from '../../../../modules/testsuites';
import Modal from '../../../../ui/layout/Modal';
import SearchBar from '../../../../ui/primitives/SearchBar';
import CustomSelect from '../../../../ui/primitives/CustomSelect';
import ActionButton from '../../../../ui/primitives/ActionButton';
import TableComponent from '../../../../ui/data/table/TableComponent';

const headers = [
    { key: 'key', label: 'Key', sortable: true },
    { key: 'summary', label: 'Summary', sortable: true },
    { key: 'suite', label: 'Test Suite', sortable: true },
    { key: 'execution_order_no', label: 'Order', sortable: false, width: '60px' },
];

const TestCaseTable = ({
    entityId,
    projectId,
    addNotification,
    selectedTestCases,
    setSelectedTestCases,
    show,
    handleCloseButton,
    handleAddTestCases,
    setFetchedAssociations,
}) => {

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [selectedTestSuite, setSelectedTestSuite] = useState('all');
    const [testSuites, setTestSuites] = useState([]);
    const [order, setOrder] = useState('asc');
    const [availableTestSuiteIds, setAvailableTestSuiteIds] = useState(new Set());
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    const suiteIdsFetchedRef = useRef(false);

    const debouncedSearch = useDebounce(searchValue, 400);

    useEffect(() => {
        getTestSuites(projectId, 0, 'all', '', 'active')
            .then(res => setTestSuites(res.data))
            .catch(() => { });
    }, [projectId]);

    useEffect(() => {
        if (show) {
            suiteIdsFetchedRef.current = false;
        }
    }, [show, debouncedSearch]);

    const fetchAvailableSuiteIds = useCallback(async (search) => {
        try {
            const res = await fetchAssociatedTestCases(
                entityId,
                projectId,
                search,
                [],
                'Approved',
                null,
                1,
                'all',
                true,
            );
            const list = res?.associations || [];
            const suiteIds = new Set();
            list.forEach(association => {
                const suites = association?.testcase?.test_suites || [];
                suites.forEach(suiteId => suiteIds.add(suiteId));
            });
            setAvailableTestSuiteIds(suiteIds);
        } catch {
        }
    }, [entityId, projectId]);

    const fetchLinked = useCallback(async (search, suite, currentPage, currentPerPage) => {
        setLoading(true);
        try {
            const suiteFilter = suite === 'all' ? [] : [suite];

            const res = await fetchAssociatedTestCases(
                entityId,
                projectId,
                search,
                suiteFilter,
                'Approved',
                null,
                currentPage,
                currentPerPage,
                true,
            );

            const list = res?.associations || [];
            setData(list);
            setTotalCount(res?.total || list.length);
            setFetchedAssociations(prev => {
                const merged = [...prev, ...list];

                return Array.from(
                    new Map(merged.map(item => [item.id, item])).values()
                );
            });
        } catch (err) {
            addNotification('danger', 'Failed to fetch test cases.');
        } finally {
            setLoading(false);
        }
    }, [entityId, projectId, addNotification, setFetchedAssociations]);

    const filteredTestSuites = useMemo(() => {
        return testSuites.filter(suite => availableTestSuiteIds.has(suite.id));
    }, [testSuites, availableTestSuiteIds]);

    // Reset to page 1 when search, suite filter, or perPage changes
    useEffect(() => {
        setPage(1);
    }, [debouncedSearch, selectedTestSuite, perPage]);

    useEffect(() => {
        if (!show || suiteIdsFetchedRef.current) return;
        suiteIdsFetchedRef.current = true;
        fetchAvailableSuiteIds(debouncedSearch);
    }, [show, debouncedSearch, fetchAvailableSuiteIds]);

    useEffect(() => {
        if (!show) return;
        fetchLinked(debouncedSearch, selectedTestSuite, page, perPage);
    }, [show, debouncedSearch, selectedTestSuite, page, perPage, fetchLinked]);

    const suiteOptions = useMemo(() => [
        { id: 'all', name: 'All Suites' },
        ...filteredTestSuites.map(s => ({ id: s.id, name: s.name })),
    ], [filteredTestSuites]);

    const baseRows = useMemo(() => {
        return data.map(a => {
            const testCaseSuiteIds = a.testcase?.test_suites || [];
            const suiteNames = testCaseSuiteIds
                .map(suiteId => {
                    const suite = testSuites.find(s => s.id === suiteId);
                    return suite?.name || '';
                })
                .filter(Boolean)
                .join(', ');

            return {
                id: a.id,
                key: a.testcase?.key || '',
                summary: a.testcase?.summary || '',
                suite: suiteNames || 'No Suite',
                assignedTo: a?.assigned_to ?? a?.assignedTo ?? null,
            };
        });
    }, [data, testSuites]);

    const selectedIndexMap = useMemo(() => {
        const map = new Map();
        selectedTestCases.forEach((id, idx) => map.set(id, idx + 1));
        return map;
    }, [selectedTestCases]);

    const tableData = useMemo(() =>
        baseRows.map(row => ({
            ...row,
            execution_order_no: selectedIndexMap.has(row.id)
                ? `#${selectedIndexMap.get(row.id)}`
                : '',
        })),
    [baseRows, selectedIndexMap]);

    const handlePerPageChange = (e) => {
        setPerPage(e.target.value);
    };

    return (
        <Modal
            modalTitle="Select Test Case(s)"
            open={show}
            handleClose={handleCloseButton}
            width="md"
            action={() => (
                <>
                    <ActionButton
                        label="Close"
                        intent="secondary"
                        onClick={handleCloseButton}
                    />
                    <ActionButton
                        label={`Add (${selectedTestCases.length})`}
                        intent="primary"
                        disabled={selectedTestCases.length === 0}
                        onClick={handleAddTestCases}
                    />
                </>
            )}
        >
            <Grid container spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <Grid item xs={12} sm={7}>
                    <SearchBar
                        value={searchValue}
                        onChange={e => setSearchValue(e.target.value)}
                        clearSearch={() => setSearchValue('')}
                        sx={{ width: '100%' }}
                    />
                </Grid>
                <Grid item xs={12} sm={5}>
                    <CustomSelect
                        id="test-suite-filter"
                        value={selectedTestSuite}
                        onChange={val => setSelectedTestSuite(val)}
                        options={suiteOptions}
                    />
                </Grid>
            </Grid>

            <TableComponent
                data={tableData}
                headerTabs={headers}
                isLoading={loading}
                order={order}
                setOrder={setOrder}
                striped
                showCheckbox
                selected={selectedTestCases}
                setSelected={setSelectedTestCases}
                tablekey="test cases"
                showPages
                page={page}
                perPage={perPage}
                totalCount={totalCount}
                handlePageChange={setPage}
                showCount
                showRowCount
                setPerPage={handlePerPageChange}
            />
        </Modal>
    );
};

export default TestCaseTable;